import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6nef4h4a3uojp5fh540', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ email: '', phone: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = "w-full bg-transparent border-b border-gray-700 text-white py-3 text-sm focus:outline-none transition-colors placeholder-gray-600";

  return (
    <section id="contact" className="py-20 md:py-32 bg-black relative overflow-hidden">
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] opacity-8 pointer-events-none" style={{ background: 'radial-gradient(circle, #ec4899 0%, transparent 70%)', filter: 'blur(100px)' }}></div>
      <div className="absolute top-0 right-0 w-[400px] h-[400px] opacity-8 pointer-events-none" style={{ background: 'radial-gradient(circle, #06b6d4 0%, transparent 70%)', filter: 'blur(100px)' }}></div>

      <div className="max-w-7xl mx-auto px-6 md:px-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-12 md:gap-16">
          {/* 문의 폼 */}
          <div className="md:col-span-2">
            <p className="text-sm font-medium tracking-[0.4em] uppercase mb-4" style={{ color: '#ec4899', textShadow: '0 0 8px #ec4899' }}>
              CONTACT US
            </p>
            <h2
              className="text-3xl md:text-5xl font-black mb-4 leading-tight"
              style={{ fontFamily: "'Orbitron', sans-serif", color: '#fff' }}
            >
              문의<span style={{ color: '#ec4899', textShadow: '0 0 15px #ec4899' }}>하기</span>
            </h2>
            <p className="text-sm text-gray-400 mb-8 md:mb-10 leading-relaxed">
              궁금하신 사항이나 예약 문의는 언제든지 연락주세요.<br />
              24시간 상담 가능합니다.
            </p>

            <form onSubmit={handleSubmit} data-readdy-form id="contact_form" className="space-y-6 md:space-y-7">
              <div>
                <label className="block text-xs text-gray-500 mb-2 tracking-wider">이메일</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                  required
                  className={inputClass}
                  onFocus={e => (e.currentTarget.style.borderColor = '#ec4899')}
                  onBlur={e => (e.currentTarget.style.borderColor = '#374151')}
                  placeholder="이메일을 입력해주세요"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-2 tracking-wider">연락처</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={e => setFormData({ ...formData, phone: e.target.value })}
                  required
                  className={inputClass}
                  onFocus={e => (e.currentTarget.style.borderColor = '#ec4899')}
                  onBlur={e => (e.currentTarget.style.borderColor = '#374151')}
                  placeholder="연락처를 입력해주세요"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-2 tracking-wider">문의 내용</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={e => setFormData({ ...formData, message: e.target.value })}
                  required
                  maxLength={500}
                  rows={4}
                  className={`${inputClass} resize-none`}
                  onFocus={e => (e.currentTarget.style.borderColor = '#ec4899')}
                  onBlur={e => (e.currentTarget.style.borderColor = '#374151')}
                  placeholder="문의 내용을 입력해주세요 (최대 500자)"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full md:w-auto px-8 py-3 rounded-full font-semibold text-black transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'linear-gradient(135deg, #ec4899, #f97316)', boxShadow: '0 0 20px rgba(236,72,153,0.5)' }}
              >
                {isSubmitting ? '전송 중...' : '문의 보내기'}
              </button>

              {submitStatus === 'success' && (
                <p className="text-sm" style={{ color: '#06b6d4', textShadow: '0 0 8px #06b6d4' }}>
                  ✓ 문의가 성공적으로 전송되었습니다!
                </p>
              )}
              {submitStatus === 'error' && (
                <p className="text-red-400 text-sm">전송 중 오류가 발생했습니다. 다시 시도해주세요.</p>
              )}
            </form>
          </div>

          {/* 푸터 링크 */}
          <div className="md:col-span-3 grid grid-cols-3 gap-6 md:gap-12 md:pt-24">
            <div>
              <h3 className="text-sm font-bold mb-4 md:mb-6 tracking-wider" style={{ color: '#ec4899' }}>서비스</h3>
              <ul className="space-y-2 md:space-y-3">
                {['VIP 서비스', '호스트 매칭', '예약 시스템', '갤러리'].map((item, i) => (
                  <li key={i}>
                    <a href={`#${['services','services','services','gallery'][i]}`} className="text-gray-500 hover:text-pink-400 transition-colors text-xs md:text-sm cursor-pointer">
                      {item}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-bold mb-4 md:mb-6 tracking-wider" style={{ color: '#06b6d4' }}>정보</h3>
              <ul className="space-y-2 md:space-y-3">
                {['회사 소개', '고객 후기', '오시는 길', '채용 정보'].map((item, i) => (
                  <li key={i}>
                    <a href={`#${['about','testimonials','contact','contact'][i]}`} className="text-gray-500 hover:text-cyan-400 transition-colors text-xs md:text-sm cursor-pointer">
                      {item}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-bold mb-4 md:mb-6 tracking-wider" style={{ color: '#a855f7' }}>연락처</h3>
              <ul className="space-y-2 md:space-y-3">
                <li className="text-gray-500 text-xs md:text-sm">부산광역시</li>
                <li className="text-gray-500 text-xs md:text-sm">010-5781-5340</li>
                <li className="text-gray-500 text-xs md:text-sm hidden md:block">info@busanhost.com</li>
                <li className="text-gray-500 text-xs md:text-sm">24시간 연중무휴</li>
              </ul>
            </div>
          </div>
        </div>

        {/* 하단 구분선 */}
        <div className="mt-14 md:mt-20 pt-8 border-t border-gray-900 text-center">
          <div className="w-full h-px mb-6 md:mb-8" style={{ background: 'linear-gradient(90deg, transparent, #ec4899, #06b6d4, transparent)' }}></div>

          {/* SNS 외부 링크 */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <a
              href="https://www.instagram.com/"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-700 text-gray-400 hover:border-pink-500 hover:text-pink-400 transition-all cursor-pointer"
              style={{ background: 'rgba(255,255,255,0.03)' }}
              aria-label="인스타그램"
            >
              <i className="ri-instagram-line text-lg" />
            </a>
            <a
              href="https://pf.kakao.com/"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-700 text-gray-400 hover:border-yellow-400 hover:text-yellow-300 transition-all cursor-pointer"
              style={{ background: 'rgba(255,255,255,0.03)' }}
              aria-label="카카오톡 채널"
            >
              <i className="ri-kakao-talk-fill text-lg" />
            </a>
            <a
              href="https://blog.naver.com/"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-700 text-gray-400 hover:border-green-400 hover:text-green-300 transition-all cursor-pointer"
              style={{ background: 'rgba(255,255,255,0.03)' }}
              aria-label="네이버 블로그"
            >
              <i className="ri-article-line text-lg" />
            </a>
            <a
              href="https://map.naver.com/"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-700 text-gray-400 hover:border-green-500 hover:text-green-400 transition-all cursor-pointer"
              style={{ background: 'rgba(255,255,255,0.03)' }}
              aria-label="네이버지도"
            >
              <i className="ri-map-pin-line text-lg" />
            </a>
            <a
              href="https://maps.google.com/maps?q=부산호스트바"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-700 text-gray-400 hover:border-red-400 hover:text-red-300 transition-all cursor-pointer"
              style={{ background: 'rgba(255,255,255,0.03)' }}
              aria-label="구글맵"
            >
              <i className="ri-global-line text-lg" />
            </a>
          </div>

          <p className="text-gray-700 text-xs tracking-widest">
            © 2024 부산호스트바. All rights reserved.
          </p>
        </div>
      </div>
    </section>
  );
}
